import { useCallback, useEffect, useRef } from 'react';

import { NotificationState } from 'components/shared/Notification';
import useDenialListImportChannel, { DenialListImportChannelMessage } from 'hooks/useDenialListImportChannel';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import { simpleDateAtTime } from 'lib/date';
import { DenialListImport, isTerminalImportStatus } from 'services/api/admin/denialListImports';
import { useNotificationActions } from 'stores/notificationStore';

type Props = {
  denialListImport: DenialListImport;
  onSettled: (importId: string) => void;
};

/**
 * Drives one upload's processing -> success/failure notification by subscribing to the import's
 * status channel (mirrors AIJobNotificationManager). The channel transmits the current state on
 * subscribe, so an import that finished before the socket connected isn't missed. The notification
 * lives in the global store, so it persists after this tracker unmounts on settle.
 */
export default function DenialListImportTracker({ denialListImport, onSettled }: Props) {
  const { addNotification, updateNotification } = useNotificationActions();
  const invalidateQueries = useInvalidateQueries();

  const settledRef = useRef(false);
  const notificationIdRef = useRef<string | null>(null);
  const filename = denialListImport.filename ?? 'denial list';

  // Add the processing notification once on mount. This effect runs before the channel
  // subscription's effect (declared below), so the id is set before any status message arrives.
  // The ref guard keeps React StrictMode's double-invoked mount effect from adding it twice.
  useEffect(() => {
    if (notificationIdRef.current) return;

    notificationIdRef.current = addNotification({
      id: denialListImport.id,
      state: NotificationState.PROCESSING,
      title: 'Currently processing',
      timestamp: `${filename} - ${simpleDateAtTime(new Date())}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMessage = useCallback(
    (data: DenialListImportChannelMessage) => {
      if (settledRef.current || !notificationIdRef.current || !isTerminalImportStatus(data.status)) return;

      settledRef.current = true;
      const notificationId = notificationIdRef.current!;

      if (data.status === 'completed') {
        // The new list now exists: refresh the tab (patient_count / Last Updated) and drop this
        // client from the eligible-clients picker. Mirrors the "Add New List" flow — the picker is a
        // disabled infinite query, so this resetQueries (not invalidate) it so the next open refetches.
        invalidateQueries({ adminDenialLists: true, denialListEligibleClients: true });
        updateNotification(notificationId, {
          state: NotificationState.SUCCESS,
          title: `${filename} has successfully been uploaded.`,
          timestamp: undefined,
        });
      } else {
        updateNotification(notificationId, {
          state: NotificationState.FAILURE,
          title: `Upload failed to process for ${filename}`,
          // Surface the specific reason (row #/header/problem) so the implementation team can fix the CSV.
          timestamp: data.error ?? undefined,
        });
      }

      onSettled(denialListImport.id);
    },
    [filename, invalidateQueries, updateNotification, onSettled, denialListImport.id]
  );

  useDenialListImportChannel({
    params: { denial_list_import_id: denialListImport.id },
    onMessageReceived: handleMessage,
  });

  return null;
}
